import { sortRows } from "../components/DataGrid/sortRows.js";
import { createExpenseGridColumns } from "../features/expenses/expenseGridColumns.js";
import { buildExpenseSearchText } from "./expenseSearchText.js";
import { matchesExpenseSearchQuery } from "./expenseSearchQuery.js";

export function compactRows(rows) {
  return Array.isArray(rows) ? rows.filter(Boolean) : [];
}

function parseRowDate(row) {
  if (!row?.date) {
    return null;
  }

  const parsed = new Date(`${row.date}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isSameMonth(date, year, monthIndex) {
  return date.getFullYear() === year && date.getMonth() === monthIndex;
}

function filterRowsByVisibility(rows, visibilityFilter, visibilityOptions) {
  if (visibilityFilter === visibilityOptions.all) {
    return rows;
  }

  if (visibilityFilter === visibilityOptions.last10) {
    return rows.slice(0, 10);
  }

  if (visibilityFilter === visibilityOptions.last30) {
    return rows.slice(0, 30);
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const previousMonthDate = new Date(currentYear, currentMonth - 1, 1);
  const previousMonthYear = previousMonthDate.getFullYear();
  const previousMonthIndex = previousMonthDate.getMonth();

  return rows.filter((row) => {
    const rowDate = parseRowDate(row);
    if (!rowDate) {
      return false;
    }

    if (visibilityFilter === "this-month") {
      return isSameMonth(rowDate, currentYear, currentMonth);
    }

    if (visibilityFilter === "last-month") {
      return isSameMonth(rowDate, previousMonthYear, previousMonthIndex);
    }

    if (visibilityFilter === "this-and-last-month") {
      return isSameMonth(rowDate, currentYear, currentMonth)
        || isSameMonth(rowDate, previousMonthYear, previousMonthIndex);
    }

    if (visibilityFilter === visibilityOptions.last10Days) {
      const threshold = new Date(today);
      threshold.setDate(threshold.getDate() - 9);
      return rowDate >= threshold && rowDate <= today;
    }

    if (visibilityFilter === visibilityOptions.last30Days) {
      const threshold = new Date(today);
      threshold.setDate(threshold.getDate() - 29);
      return rowDate >= threshold && rowDate <= today;
    }

    return true;
  });
}

function rowMatchesExpenseSearch(row, expenseTypeLabels, expenseGridColumns, searchQueryGroups) {
  return matchesExpenseSearchQuery(
    buildExpenseSearchText(row, expenseTypeLabels, expenseGridColumns),
    searchQueryGroups
  );
}

export function buildAppliedVisibleRowIds(
  rows,
  visibilityFilter,
  searchQueryGroups,
  expenseTypeLabels,
  expenseGridColumns,
  isCompleteRow,
  visibilityOptions
) {
  const safeRows = compactRows(rows);
  const persistedRows = safeRows.filter(isCompleteRow);
  const visibilityFilteredRows = filterRowsByVisibility(persistedRows, visibilityFilter, visibilityOptions);
  const matchingRows = searchQueryGroups.length === 0
    ? visibilityFilteredRows
    : visibilityFilteredRows.filter((row) => (
      rowMatchesExpenseSearch(row, expenseTypeLabels, expenseGridColumns, searchQueryGroups)
    ));

  return new Set(matchingRows.map((row) => row.id).filter(Boolean));
}

export function syncAppliedVisibleRowIds(currentVisibleRowIds, currentVisibleRows, nextVisibleRows, isCompleteRow) {
  const nextVisibleSet = new Set(currentVisibleRowIds ?? []);

  currentVisibleRows
    .filter(isCompleteRow)
    .map((row) => row.id)
    .filter(Boolean)
    .forEach((rowId) => {
      nextVisibleSet.delete(rowId);
    });

  nextVisibleRows
    .filter(isCompleteRow)
    .map((row) => row.id)
    .filter(Boolean)
    .forEach((rowId) => {
      nextVisibleSet.add(rowId);
    });

  return nextVisibleSet;
}

function currentDateInputValueForScope(scopedYear) {
  const now = new Date();
  const currentYear = String(now.getFullYear());

  if (scopedYear && String(scopedYear) !== currentYear) {
    return `${scopedYear}-01-01`;
  }

  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
}

export function sortExpenseRowsNewestFirst(rows) {
  return compactRows(rows)
    .map((row, index) => ({ row, index }))
    .slice()
    .sort((left, right) => {
      const dateComparison = String(right.row.date ?? "").localeCompare(String(left.row.date ?? ""));
      if (dateComparison !== 0) {
        return dateComparison;
      }

      const createdAtComparison = String(right.row.createdAt ?? "").localeCompare(String(left.row.createdAt ?? ""));
      if (createdAtComparison !== 0) {
        return createdAtComparison;
      }

      return left.index - right.index;
    })
    .map((entry) => entry.row);
}

export function createEmptyExpenseRow(scopedYear) {
  return {
    id: crypto.randomUUID(),
    date: currentDateInputValueForScope(scopedYear),
    amount: 0,
    method: "",
    type: "",
    supplier: "",
    categoryId: "",
    category: "",
    service: ""
  };
}

export function createDuplicateExpenseRow(row, scopedYear) {
  return {
    ...row,
    id: crypto.randomUUID(),
    date: currentDateInputValueForScope(scopedYear),
    createdAt: new Date().toISOString()
  };
}

export function isEmptyExpenseRow(row) {
  return Number(row.amount ?? 0) === 0
    && !String(row.method ?? "").trim()
    && !String(row.type ?? "").trim()
    && !String(row.supplier ?? "").trim()
    && !String(row.categoryId ?? "").trim()
    && !String(row.service ?? "").trim();
}

export function isCompleteExpenseRow(row) {
  return Boolean(String(row.date ?? "").trim())
    && Number.isFinite(Number(row.amount))
    && Boolean(String(row.method ?? "").trim())
    && Boolean(String(row.type ?? "").trim())
    && Boolean(String(row.supplier ?? "").trim())
    && Boolean(String(row.categoryId ?? "").trim());
}

export function buildExpenseTableView({
  activeYear,
  appliedVisibleRowIds,
  categoryOptions,
  rows,
  searchQueryGroups,
  sortState,
  supplierOptions,
  serviceOptions,
  paymentMethodOptions,
  t,
  visibilityFilter,
  expenseTypeLabels,
  visibilityOptions
}) {
  const expenseGridColumns = createExpenseGridColumns({
    activeYear,
    categoryOptions,
    paymentMethodOptions,
    supplierOptions,
    serviceOptions,
    t
  });
  const expenseHeaderGroups = [
    {
      key: "date",
      label: t("date"),
      sortKey: "dateMonth",
      columns: ["dateMonth", "dateDay"],
      columnLabels: {
        dateMonth: t("month"),
        dateDay: t("day")
      }
    },
    { key: "amount", label: t("amount"), columns: ["amount"] },
    { key: "method", label: t("method"), columns: ["method"] },
    { key: "type", label: t("type"), columns: ["type"] },
    { key: "category", label: t("category"), columns: ["categoryId"] },
    { key: "supplier", label: t("supplier"), columns: ["supplier"] },
    { key: "service", label: t("service"), columns: ["service"] }
  ];
  const matchingRowsAcrossAll = (() => {
    const safeRows = compactRows(rows);
    const persistedRows = safeRows.filter(isCompleteExpenseRow);

    if (searchQueryGroups.length === 0) {
      return persistedRows;
    }

    return persistedRows.filter((row) => (
      rowMatchesExpenseSearch(row, expenseTypeLabels, expenseGridColumns, searchQueryGroups)
    ));
  })();
  const filteredRows = (() => {
    const safeRows = compactRows(rows);
    const draftRows = safeRows.filter((row) => !isCompleteExpenseRow(row));
    const persistedRows = safeRows.filter(isCompleteExpenseRow);
    const visiblePersistedRows = persistedRows.filter((row) => appliedVisibleRowIds.has(row.id));

    return [...draftRows, ...visiblePersistedRows];
  })();
  const visibleRows = [
    ...filteredRows.filter((row) => !isCompleteExpenseRow(row)),
    ...sortRows(filteredRows.filter(isCompleteExpenseRow), expenseGridColumns, sortState)
  ];
  const hiddenMatchingCount = (() => {
    if (searchQueryGroups.length === 0 || visibilityFilter === visibilityOptions.all) {
      return 0;
    }

    const visibleMatchingRows = visibleRows.filter((row) => (
      isCompleteExpenseRow(row)
      && rowMatchesExpenseSearch(row, expenseTypeLabels, expenseGridColumns, searchQueryGroups)
    ));

    return Math.max(0, matchingRowsAcrossAll.length - visibleMatchingRows.length);
  })();
  const visibleTotal = visibleRows
    .filter(isCompleteExpenseRow)
    .reduce((sum, row) => sum + Number(row.amount ?? 0), 0);

  return {
    expenseGridColumns,
    expenseHeaderGroups,
    hiddenMatchingCount,
    visibleRows,
    visibleTotal
  };
}
